import type { PropsWithChildren } from 'react';

import { cva } from 'cva';

import { cx } from '../../utils/cx';

import styles from './skeleton.module.css';

const skeletonVariants = cva({
  base: styles.root,
  variants: {
    layout: {
      block: styles.layout_block,
      inline: styles.layout_inline,
    },
    radius: {
      '2xs': styles.radius_2xs,
      xs: styles.radius_xs,
      s: styles.radius_s,
      m: styles.radius_m,
      l: styles.radius_l,
      xl: styles.radius_xl,
      '2xl': styles.radius_2xl,
      full: styles.radius_full,
    },
  },
  defaultVariants: {
    layout: 'block',
    radius: 's',
  },
});

export type SkeletonLayout = 'block' | 'inline';

export type SkeletonRadius =
  | '2xs'
  | 'xs'
  | 's'
  | 'm'
  | 'l'
  | 'xl'
  | '2xl'
  | 'full';

type SkeletonBaseProps = {
  className?: string;
  isLoading?: boolean;
  layout?: SkeletonLayout;
  radius?: SkeletonRadius;
  width?: number | string;
  height?: number | string;
  aspectRatio?: string;
};

type SkeletonWithChildrenProps = SkeletonBaseProps & PropsWithChildren;

type SkeletonFixedSizeProps = SkeletonBaseProps & {
  children?: never;
};

export type SkeletonProps = SkeletonWithChildrenProps | SkeletonFixedSizeProps;

/**
 * A loading placeholder that masks wrapped content with a shimmer effect.
 *
 * You can also render a fixed-size placeholder by omitting children and
 * providing width and height.
 *
 * Use `layout="block"` (default) to preserve normal block layout behavior.
 * Use `layout="inline"` for content-sized placeholders.
 *
 * Pass `isLoading={false}` to render the children without the skeleton effect.
 *
 * ```tsx
 * <Skeleton radius="m" layout="inline">
 *   <Heading>Deserunt esse quis ea nostrud</Heading>
 * </Skeleton>
 *
 * <Skeleton width="24px" height="24px" radius="full" />
 * ```
 */
export function Skeleton(props: SkeletonProps) {
  const {
    className,
    isLoading = true,
    layout = 'block',
    radius = 's',
    width,
    height,
    aspectRatio,
    children,
  } = props;

  if (!isLoading) {
    return children ?? null;
  }

  return (
    <div
      className={cx([className, skeletonVariants({ layout, radius })])}
      style={{ width, height, aspectRatio }}
      aria-busy={isLoading}
      inert
    >
      <div className={styles.loading_layer} aria-hidden={true} />
      <div className={styles.masked_content} aria-hidden={true}>
        {children}
      </div>
    </div>
  );
}
